/**
 * @licita-mind/shared-types
 *
 * schemas zod compartilhados entre api e web.
 * fonte unica de verdade dos contratos http (request/response).
 *
 * regra: enums duplicados aqui sao validados em compile time contra
 * os enums do @licita-mind/domain via type-assertions em testes do api
 * (evita acoplamento circular shared-types -> domain -> shared-types).
 */
import { z } from 'zod';
// ============ infra ============
export const HealthResponseSchema = z.object({
    status: z.literal('ok'),
    service: z.string(),
    uptime: z.number(),
    timestamp: z.string().datetime(),
});
// ============ enums (espelham @licita-mind/domain) ============
export const StatusPesquisaSchema = z.enum([
    'RASCUNHO',
    'COLETANDO_PRECOS',
    'CESTA_FORMADA',
    'JUSTIFICATIVA_GERADA',
    'DOCUMENTO_ASSINADO',
    'FINALIZADA',
    'CANCELADA',
]);
// ============ value-object schemas reutilizaveis ============
/** uuid v4 ou v7 — ambos aceitos */
export const UuidSchema = z.string().uuid();
const Catmat = z
    .string()
    .regex(/^[0-9]{6,12}$/, 'CATMAT/CATSER precisa ter 6 a 12 digitos');
// ============ itens ============
export const ItemPesquisaInputSchema = z
    .object({
    descricao: z.string().trim().min(1).max(500),
    especificacao_tecnica: z.string().trim().max(2000).nullable().optional(),
    unidade: z.string().trim().min(1).max(20),
    quantidade: z.number().positive().finite(),
    catmat: Catmat.nullable().optional(),
    catser: Catmat.nullable().optional(),
})
    .refine((v) => !(v.catmat && v.catser), {
    message: 'item nao pode ter catmat e catser simultaneamente',
    path: ['catser'],
});
export const ItemPesquisaSchema = z.object({
    id: UuidSchema,
    descricao: z.string(),
    especificacao_tecnica: z.string().nullable(),
    unidade: z.string(),
    quantidade: z.number(),
    catmat: z.string().nullable(),
    catser: z.string().nullable(),
    criado_em: z.string().datetime(),
});
// ============ pesquisas ============
export const PesquisaCreateSchema = z.object({
    objeto: z.string().trim().min(1).max(500),
    pesquisa_origem_id: UuidSchema.nullable().optional(),
    itens: z.array(ItemPesquisaInputSchema).min(1).max(200),
});
export const PesquisaListItemSchema = z.object({
    id: UuidSchema,
    objeto: z.string(),
    status: StatusPesquisaSchema,
    qtd_itens: z.number().int().nonnegative(),
    pesquisa_origem_id: UuidSchema.nullable(),
    criada_em: z.string().datetime(),
    atualizada_em: z.string().datetime(),
});
export const PesquisaListResponseSchema = z.object({
    data: z.array(PesquisaListItemSchema),
    /** cursor pra proxima pagina (criada_em ISO + id), null se acabou */
    next_cursor: z.string().nullable(),
});
export const PesquisaDetailSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    agente_responsavel_id: UuidSchema,
    objeto: z.string(),
    status: StatusPesquisaSchema,
    pesquisa_origem_id: UuidSchema.nullable(),
    criada_em: z.string().datetime(),
    atualizada_em: z.string().datetime(),
    itens: z.array(ItemPesquisaSchema),
});
export const TransicionarStatusSchema = z.object({
    novo_status: StatusPesquisaSchema,
});
export const PesquisaListQuerySchema = z.object({
    status: StatusPesquisaSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    cursor: z.string().nullable().optional(),
});
// ============ cotacoes ============
export const FontePesquisaSchema = z.enum([
    'PNCP',
    'PAINEL_PRECOS',
    'CONTRATACAO_SIMILAR_MUNICIPIO',
    'SITIO_ESPECIALIZADO',
    'COTACAO_DIRETA_FORNECEDOR',
]);
export const StatusCotacaoSchema = z.enum([
    'VALIDA',
    'EXCLUIDA_OUTLIER',
    'INVALIDA_POR_IDADE',
    'EXCLUIDA_MANUALMENTE',
]);
const CnpjDigitos = z.string().regex(/^[0-9]{14}$/, 'CNPJ precisa ter 14 digitos');
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'data ISO YYYY-MM-DD');
export const CotacaoCreateSchema = z.object({
    fonte: FontePesquisaSchema,
    fonte_url: z.string().url().max(2000).nullable().optional(),
    fornecedor_cnpj: CnpjDigitos.nullable().optional(),
    fornecedor_nome: z.string().trim().min(1).max(200).nullable().optional(),
    /** preco unitario em centavos (Money do domain) */
    preco_unitario_centavos: z.number().int().nonnegative(),
    data_referencia: IsoDate,
    referencia_externa: z.record(z.unknown()).default({}),
    metadata_coleta: z.record(z.unknown()).default({}),
});
export const CotacaoSchema = z.object({
    id: UuidSchema,
    item_id: UuidSchema,
    fonte: FontePesquisaSchema,
    fonte_url: z.string().nullable(),
    fornecedor_cnpj: z.string().nullable(),
    fornecedor_nome: z.string().nullable(),
    preco_unitario_centavos: z.number().int().nonnegative(),
    moeda: z.string(),
    data_referencia: IsoDate,
    coletada_em: z.string().datetime(),
    status: StatusCotacaoSchema,
    motivo_exclusao: z.string().nullable(),
    referencia_externa: z.record(z.unknown()),
    metadata_coleta: z.record(z.unknown()),
});
export const CotacaoListResponseSchema = z.object({
    data: z.array(CotacaoSchema),
});
export const CotacaoExcluirSchema = z.object({
    motivo: z.string().trim().min(1, 'motivo obrigatorio').max(500),
    tipo: z.enum(['manual']).default('manual'),
});
// ============ cestas ============
export const MetodoEstatisticoSchema = z.enum([
    'MEDIANA',
    'MEDIA_SANEADA_TUKEY',
    'MEDIA_SANEADA_ZSCORE',
]);
export const CestaFormarSchema = z.object({
    metodo: MetodoEstatisticoSchema.default('MEDIA_SANEADA_TUKEY'),
    /** texto obrigatorio (TCU 1.875/2021 — metodologia justificada) */
    justificativa_metodo: z.string().trim().min(1).max(2000),
});
export const EstatisticasCestaSchema = z.object({
    minimo: z.number().int().nonnegative(),
    maximo: z.number().int().nonnegative(),
    mediana: z.number().int().nonnegative(),
    media: z.number().int().nonnegative(),
    desvioPadrao: z.number().int().nonnegative(),
    q1: z.number().int().nonnegative(),
    q3: z.number().int().nonnegative(),
    iqr: z.number().int().nonnegative(),
    cotacoesValidas: z.number().int().nonnegative(),
    cotacoesExcluidas: z.number().int().nonnegative(),
});
export const CestaCotacaoSchema = z.object({
    cotacao_id: UuidSchema,
    incluida: z.boolean(),
    motivo_exclusao: z.string().nullable(),
    ordem: z.number().int().nonnegative(),
});
export const CestaDetailSchema = z.object({
    id: UuidSchema,
    item_id: UuidSchema,
    metodo: MetodoEstatisticoSchema,
    preco_referencia_centavos: z.number().int().nonnegative(),
    estatisticas: EstatisticasCestaSchema,
    justificativa_metodo: z.string(),
    formada_em: z.string().datetime(),
    cotacoes: z.array(CestaCotacaoSchema),
});
// ============ itens similares (busca semantica) ============
export const ItemSimilarSchema = z.object({
    id: UuidSchema,
    pesquisa_id: UuidSchema,
    pesquisa_objeto: z.string(),
    descricao: z.string(),
    especificacao_tecnica: z.string().nullable(),
    unidade: z.string(),
    quantidade: z.number(),
    catmat: z.string().nullable(),
    catser: z.string().nullable(),
    /** distancia cosseno [0, 2]; menor = mais similar */
    distancia: z.number(),
    qtd_cotacoes_validas: z.number().int().nonnegative(),
    preco_referencia_centavos: z.number().int().nonnegative().nullable(),
});
export const ItensSimilaresResponseSchema = z.object({
    data: z.array(ItemSimilarSchema),
});
// ============ analise de risco TCU ============
export const SeveridadeSchema = z.enum(['BAIXA', 'MEDIA', 'ALTA']);
export const AcordaoTcuSchema = z.object({
    id: z.string(),
    numero: z.string(),
    orgao: z.string(),
    relator: z.string(),
    tema: z.string(),
    ementa: z.string(),
    url: z.string().url(),
});
export const AlertaRiscoSchema = z.object({
    regra_id: z.string(),
    severidade: SeveridadeSchema,
    mensagem: z.string(),
    fundamento_legal: z.object({
        acordao: AcordaoTcuSchema,
        trecho: z.string().optional(),
    }),
    contexto: z.record(z.union([z.string(), z.number()])).optional(),
});
export const AnaliseRiscoResponseSchema = z.object({
    item_descricao: z.string(),
    alertas: z.array(AlertaRiscoSchema),
    resumo: z.object({
        total: z.number().int().nonnegative(),
        alta: z.number().int().nonnegative(),
        media: z.number().int().nonnegative(),
        baixa: z.number().int().nonnegative(),
    }),
});
// ============ documento de pesquisa ============
export const DocumentoPesquisaSchema = z.object({
    id: UuidSchema,
    pesquisa_id: UuidSchema,
    agente_id: UuidSchema,
    hash_sha256: z.string().regex(/^[0-9a-f]{64}$/),
    gerado_em: z.string().datetime(),
    tamanho_bytes: z.number().int().positive(),
});
//# sourceMappingURL=index.js.map